/**
 * Google Veo - AUTOMATED provider (spec sections 6, 9, 12, 28).
 *
 * Verified API surface (checked 2026-10-05, ai.google.dev/gemini-api/docs/veo):
 *   POST {base}/models/{model}:predictLongRunning     header: x-goog-api-key
 *   -> returns an operation; poll it until operation.done is true
 *   -> result URI: response.generateVideoResponse.generatedSamples[0].video.uri
 *
 * The browser NEVER holds the API key. Every call goes through the
 * Cloudflare Worker, which injects the secret server-side. This class
 * only ever talks to the Worker.
 */

import { VideoProvider, PROVIDER_STATUS, estimateCost } from './base.js';
import { RESEARCH } from '../core/config.js';
import { ProviderUnavailableError } from '../core/errors.js';
import { createLogger } from '../utils/log.js';

const log = createLogger('veo');

export const VEO_MODELS = RESEARCH.googleVeo.models;

export class GoogleVeoProvider extends VideoProvider {
  /**
   * @param {{ getWorkerUrl: () => string }} deps
   */
  constructor({ getWorkerUrl }) {
    super({
      id: 'google-veo',
      name: 'Google Veo - API',
      type: 'automated',
      description:
        'Automated generation through the Gemini API, proxied by your Cloudflare Worker so the API key never reaches the browser.',
      docsUrl: 'https://ai.google.dev/gemini-api/docs/veo',
      capabilities: {
        type: 'automated',
        automated: true,
        requiresApiKey: true,
        requiresWorker: true,
        manualStepRequired: false,
        asynchronous: true,
        supportsCancel: false,
        nativeAudio: true,
        durations: RESEARCH.googleVeo.durations,
        aspectRatios: RESEARCH.googleVeo.aspectRatios,
        resolutions: RESEARCH.googleVeo.resolutions,
        costModel: 'per-second',
        costNote:
          'Verified 2026-10-05 from the official pricing page: no free tier exists for any Veo model. Veo 3.1 Fast is $0.10/s at 720p, $0.12/s at 1080p. You are only charged when a video is successfully generated.',
        browserAutomation: false,
      },
    });
    this.getWorkerUrl = getWorkerUrl;
  }

  get workerUrl() {
    return String(this.getWorkerUrl?.() ?? '').replace(/\/+$/, '');
  }

  get isConfigured() {
    return Boolean(this.workerUrl);
  }

  async status() {
    if (!this.isConfigured) {
      return {
        state: PROVIDER_STATUS.UNCONFIGURED,
        detail:
          'No Worker URL is configured. Deploy worker/index.js to Cloudflare, set GEMINI_API_KEY as a Worker secret, then paste the Worker URL in Settings.',
        checkedAt: new Date().toISOString(),
        meta: { docsUrl: this.docsUrl, verifiedOn: RESEARCH.checkedOn },
      };
    }
    try {
      const response = await this.#workerFetch('/api/providers', { method: 'GET' });
      const payload = await response.json();
      const veo = payload?.providers?.find?.((p) => p.id === 'google-veo');
      return {
        state: veo?.configured ? PROVIDER_STATUS.READY : PROVIDER_STATUS.UNCONFIGURED,
        detail: veo?.configured
          ? 'The Worker is reachable and reports a configured Veo credential.'
          : 'The Worker is reachable but has no Veo credential configured.',
        checkedAt: new Date().toISOString(),
        meta: veo ?? null,
      };
    } catch (error) {
      return {
        state: PROVIDER_STATUS.ERROR,
        detail: `Could not reach the Worker: ${error.message}`,
        checkedAt: new Date().toISOString(),
      };
    }
  }

  async validateCredentials() {
    const status = await this.status();
    return {
      configured: status.state === PROVIDER_STATUS.READY,
      detail: status.detail,
      meta: status.meta,
    };
  }

  /** Cost preview used by the mandatory confirmation sheet (spec 28). */
  estimateGenerationCost({ model, resolution, durationSeconds }) {
    const price = RESEARCH.googleVeo.pricePerSecond[model];
    if (!price) return null;
    return {
      model,
      resolution,
      durationSeconds: Number(durationSeconds),
      pricePerSecond: price[resolution] ?? null,
      totalUsd: estimateCost(price, resolution, Number(durationSeconds)),
      currency: 'USD',
      note: 'Billed by Google at the published per-second rate. You are only charged when a video is generated successfully.',
    };
  }

  /**
   * Start a generation. Returns an operation handle to poll.
   * The caller MUST have shown the cost confirmation sheet first.
   */
  async generate({
    prompt,
    negativePrompt = null,
    sceneId = null,
    model = 'veo-3.1-fast-generate-preview',
    durationSeconds = '8',
    aspectRatio = '9:16',
    resolution = '720p',
    personGeneration = null,
    signal = null,
  }) {
    if (!this.isConfigured) {
      throw new ProviderUnavailableError(
        'Google Veo is not configured. Add your Cloudflare Worker URL in Settings.',
        { hint: 'See DEPLOYMENT.md for the Worker deployment steps.' },
      );
    }
    if (!prompt || prompt.trim().length < 20) {
      throw new Error('A prompt is required before calling Veo.');
    }

    log.info('starting veo generation', { model, durationSeconds, resolution, aspectRatio });

    const response = await this.#workerFetch('/api/veo/generate', {
      method: 'POST',
      body: JSON.stringify({
        model,
        prompt,
        negativePrompt,
        sceneId,
        durationSeconds,
        aspectRatio,
        resolution,
        personGeneration,
      }),
      signal,
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error ?? `The Worker returned ${response.status}.`);
    }
    return {
      operationName: payload.operationName,
      providerId: this.id,
      model,
      durationSeconds,
      aspectRatio,
      resolution,
      startedAt: new Date().toISOString(),
      cost: this.estimateGenerationCost({ model, resolution, durationSeconds }),
    };
  }

  async getGenerationStatus(operationName, { signal = null } = {}) {
    if (!this.isConfigured) throw new ProviderUnavailableError('Google Veo is not configured.');
    if (!operationName) throw new Error('An operation name is required.');
    const response = await this.#workerFetch(
      `/api/veo/status?operation=${encodeURIComponent(operationName)}`,
      { method: 'GET', signal },
    );
    const payload = await response.json();
    if (!response.ok) throw new Error(payload?.error ?? `The Worker returned ${response.status}.`);
    return {
      done: Boolean(payload.done),
      error: payload.error ?? null,
      videoUri: payload.videoUri ?? null,
      raw: payload.raw ?? null,
    };
  }

  /**
   * Download the finished clip through the Worker, so the API key stays
   * server-side and the video host is allow-listed by the Worker.
   */
  async downloadResult(videoUri, { signal = null } = {}) {
    if (!this.isConfigured) throw new ProviderUnavailableError('Google Veo is not configured.');
    if (!videoUri) throw new Error('A video URI is required.');
    const response = await this.#workerFetch(
      `/api/veo/download?uri=${encodeURIComponent(videoUri)}`,
      { method: 'GET', signal },
    );
    if (!response.ok) {
      let detail = `The Worker returned ${response.status}.`;
      try {
        detail = (await response.json())?.error ?? detail;
      } catch {
        /* keep the default */
      }
      throw new Error(detail);
    }
    const blob = await response.blob();
    return {
      blob,
      mimeType: response.headers.get('content-type') ?? blob.type ?? 'video/mp4',
      bytes: blob.size,
    };
  }

  /**
   * Poll an operation until it finishes.
   * Latency per the official docs: 11 seconds minimum, up to 6 minutes at peak.
   */
  async waitForCompletion(operationName, { intervalMs = 8000, timeoutMs = 8 * 60 * 1000, onTick, signal } = {}) {
    const startedAt = Date.now();
    for (;;) {
      if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
      const status = await this.getGenerationStatus(operationName, { signal });
      onTick?.(status);
      if (status.done) return status;
      if (Date.now() - startedAt > timeoutMs) {
        throw new Error('Timed out waiting for Veo. The operation may still be running - check the provider dashboard.');
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  async cancel() {
    // Verified: the Veo API documents no cancellation endpoint.
    return {
      cancelled: false,
      detail:
        'The Gemini API does not document a cancellation endpoint for video operations. The operation may still complete and be billed.',
    };
  }

  async #workerFetch(path, init = {}) {
    const url = `${this.workerUrl}${path}`;
    const response = await fetch(url, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(init.headers ?? {}),
      },
      signal: init.signal ?? null,
    }).catch((error) => {
      throw new Error(`Could not reach the BEX LOOP Worker at ${this.workerUrl}: ${error.message}`);
    });
    return response;
  }
}
